/**
 * A2UI Component Registry
 * Maps A2UI component types to React component implementations
 * Based on Google A2UI Protocol v0.8
 */

import React from 'react';

// Import A2UI components (will be created next)
import A2UIText from './components/A2UIText';
import A2UIButton from './components/A2UIButton';
import A2UICard from './components/A2UICard';
import A2UIRow from './components/A2UIRow';
import A2UIColumn from './components/A2UIColumn';
import A2UIImage from './components/A2UIImage';
import A2UIDivider from './components/A2UIDivider';
import A2UITextInput from './components/A2UITextInput';
import A2UISelect from './components/A2UISelect';
import A2UICheckbox from './components/A2UICheckbox';
import A2UIDateTimeInput from './components/A2UIDateTimeInput';
import A2UISlider from './components/A2UISlider';
import A2UIList from './components/A2UIList';
import A2UIIcon from './components/A2UIIcon';

/**
 * Standard A2UI Component Catalog
 * Maps component type names to React components
 */
const standardCatalog = {
  // Layout Components
  'Row': A2UIRow,
  'Column': A2UIColumn,
  'Card': A2UICard,
  'List': A2UIList,
  'Divider': A2UIDivider,
  
  // Display Components
  'Text': A2UIText,
  'Image': A2UIImage,
  'Icon': A2UIIcon,
  
  // Interactive Components
  'Button': A2UIButton,
  'TextInput': A2UITextInput,
  'Select': A2UISelect,
  'Checkbox': A2UICheckbox,
  'DateTimeInput': A2UIDateTimeInput,
  'Slider': A2UISlider,
};

class ComponentRegistry {
  constructor() {
    this.catalogs = new Map();
    // Register standard catalog
    this.registerCatalog(
      'https://github.com/google/A2UI/blob/main/specification/0.8/json/standard_catalog_definition.json',
      standardCatalog
    );
    // Also register as default
    this.registerCatalog('default', standardCatalog);
  }

  /**
   * Register a component catalog
   * @param {string} catalogId - Unique catalog identifier
   * @param {Object} components - Map of component types to React components
   */
  registerCatalog(catalogId, components) {
    this.catalogs.set(catalogId, { ...components });
  }

  /**
   * Register a custom component to a catalog
   * @param {string} catalogId - Catalog to add to
   * @param {string} typeName - Component type name
   * @param {React.Component} component - React component
   */
  registerComponent(catalogId, typeName, component) {
    if (!this.catalogs.has(catalogId)) {
      this.catalogs.set(catalogId, {});
    }
    this.catalogs.get(catalogId)[typeName] = component;
  }

  /**
   * Get a component from a catalog
   * @param {string} typeName - Component type name
   * @param {string} catalogId - Catalog ID (defaults to standard)
   * @returns {React.Component|null}
   */
  getComponent(typeName, catalogId = 'default') {
    // Try specified catalog first
    const catalog = this.catalogs.get(catalogId);
    if (catalog && catalog[typeName]) {
      return catalog[typeName];
    }
    
    // Fall back to default catalog
    const defaultCatalog = this.catalogs.get('default');
    if (defaultCatalog && defaultCatalog[typeName]) {
      return defaultCatalog[typeName];
    }
    
    console.warn(`A2UI: Unknown component type "${typeName}" in catalog "${catalogId}"`);
    return null;
  }

  /**
   * Check if a component type is registered
   */
  hasComponent(typeName, catalogId = 'default') {
    return this.getComponent(typeName, catalogId) !== null;
  }

  /**
   * Get all registered component types for a catalog
   */
  getRegisteredTypes(catalogId = 'default') {
    const catalog = this.catalogs.get(catalogId);
    return catalog ? Object.keys(catalog) : [];
  }
}

// Export singleton instance
export const componentRegistry = new ComponentRegistry();
export default ComponentRegistry;
